import React from 'react';
import Papa from 'papaparse';
import { axios } from '@/services/axios';
import { downloadCSV } from '@/utils/utils';
import { FAILED_API_FILENAME } from './constants';

const ERROR_MESSAGE = 'Something went wrong. Please try again later.';

const EXPORT_FIELDS = {
    country: 'Country',
    name: 'Name',
    prefix: 'Prefix',
    priority: 'Priority',
    minPrice: 'Minimum Cost',
    flagFallInRate: 'Flag Fall In Rate',
    flagFallOutRate: 'Flag Fall Out Rate',
    inboundRate: 'Inbound Rate',
    outboundRate: 'Outbound Rate',
    apiErrors: 'Errors',
};

export default function useBulkUpdate() {
    const [error, setError] = React.useState([]);
    const [loading, setLoading] = React.useState(false);
    const [succeed, setSucceed] = React.useState([]);
    const [counter, setCounter] = React.useState(0);
    const [errorCounter, setErrorCounter] = React.useState(0);
    const [uploadPayloads, setUploadPayloads] = React.useState([]);

    const handleReset = () => {
        setCounter(0);
        setErrorCounter(0);
        setSucceed([]);
        setError([]);
    };

    const handleFinish = ({ errors, responses }) => {
        setError(errors);
        setSucceed(responses);
    };

    const handleBulkUpdate = async (payloads) => {
        const errors = [];
        const responses = [];
        setUploadPayloads(payloads);

        for (let i = 0; i < payloads.length; i++) {
            const payload = payloads[i];
            // Strip internal-only flag before sending to API
            const { isNewRate, ...body } = payload;

            try {
                if (isNewRate) {
                    await axios.post('/internationalrate', body);
                } else {
                    await axios.put('/internationalrate', body);
                }
                responses.push({
                    data: {
                        country: body.country,
                        name: body.name,
                        status: 'Success',
                    },
                });
            } catch (e) {
                errors.push({
                    ...body,
                    apiErrors:
                        `${e?.message}\n${e?.response?.data?.message || ''}\n${e?.response?.status || ''}` ||
                        ERROR_MESSAGE,
                });
                setErrorCounter((prev) => prev + 1);
            } finally {
                setCounter((prev) => prev + 1);
                if (i === payloads.length - 1) {
                    handleFinish({ errors, responses });
                }
            }
        }
    };

    const processSubmission = (payloads) => {
        setLoading(true);
        handleBulkUpdate(payloads);
    };

    const handlePayloadChange = React.useCallback((payloads) => {
        processSubmission(payloads);
    }, []);

    const handleExportFailedData = () => {
        const rows = error.map((item) =>
            Object.keys(EXPORT_FIELDS).reduce((acc, key) => {
                acc[EXPORT_FIELDS[key]] = item[key] ?? '';
                return acc;
            }, {}),
        );
        const res = Papa.unparse(rows);
        downloadCSV(res, FAILED_API_FILENAME);
    };

    return {
        handlePayloadChange,
        loading,
        counter,
        errorCounter,
        error,
        succeed,
        handleReset,
        handleExportFailedData,
        total: uploadPayloads.length,
    };
}
